import { Matrix4, Quaternion, Vector3 } from "three";
import { EXTENSION, dataOf, nodeTree } from "./document.js";
import { resolveModelVistaNodePath } from "../logic/modelvista_extension.js";

export function hierarchyDropPlan(json, indices, targetIndex, position) {
    if (!["before", "after", "inside"].includes(position) || !indices.length) return null;
    const tree = nodeTree(json), nodes = new Map(tree.map(node => [node.index, node])), parents = new Map();
    for (const node of tree) for (const child of json.nodes[node.index].children ?? []) parents.set(child, node.index);
    const selected = new Set(indices);
    if (!nodes.has(targetIndex) || [...selected].some(index => !parents.has(index))) return null;
    if ([targetIndex, ...selected].some(index => nodes.get(index).path.split("/").some(name => name.startsWith("__INNOWORKS_MATERIAL")))) return null;
    const hasSelectedAncestor = index => {
        for (let parent = parents.get(index); parent !== undefined; parent = parents.get(parent)) if (selected.has(parent)) return true;
        return false;
    };
    if (selected.has(targetIndex) || hasSelectedAncestor(targetIndex)) return null;
    // Selected descendants travel with their selected ancestor, exactly once.
    const moving = tree.filter(node => selected.has(node.index) && !hasSelectedAncestor(node.index)).map(node => node.index);
    const parentIndex = position === "inside" ? targetIndex : parents.get(targetIndex);
    if (parentIndex === undefined) return null;
    const original = json.nodes[parentIndex].children ?? [], moved = new Set(moving);
    const children = original.filter(index => !moved.has(index));
    const insertion = position === "inside" ? children.length : children.indexOf(targetIndex) + Number(position === "after");
    children.splice(insertion, 0, ...moving);
    const reparented = moving.filter(index => parents.get(index) !== parentIndex);
    if (!reparented.length && children.every((index, i) => index === original[i])) return null;
    return { parentIndex, children, moving, reparented, parents };
}

const close = (a, b) => Math.abs(a - b) <= 1e-7 * Math.max(1, Math.abs(a), Math.abs(b));
const vectorData = v => ({ X: v.x, Y: v.y, Z: v.z });
const quaternionData = q => ({ ...vectorData(q), W: q.w });

function localMatrix(node) {
    return node.matrix ? new Matrix4().fromArray(node.matrix) : new Matrix4().compose(
        new Vector3().fromArray(node.translation ?? [0, 0, 0]),
        new Quaternion().fromArray(node.rotation ?? [0, 0, 0, 1]),
        new Vector3().fromArray(node.scale ?? [1, 1, 1]));
}

function decompose(matrix) {
    const position = new Vector3(), rotation = new Quaternion(), scale = new Vector3();
    matrix.decompose(position, rotation, scale); rotation.normalize();
    const reconstructed = new Matrix4().compose(position, rotation, scale);
    if (!matrix.elements.every((value, i) => Number.isFinite(value) && close(value, reconstructed.elements[i]))) {
        throw new Error("This move would distort the part. Choose a parent with compatible rotation and scale.");
    }
    return { position, rotation, scale };
}

function worldMatrices(json, parents, live) {
    const cache = new Map();
    const world = index => {
        if (cache.has(index)) return cache.get(index);
        const parent = parents.get(index), matrix = live?.has(index) ? new Matrix4().fromArray(live.get(index))
            : parent === undefined ? localMatrix(json.nodes[index]) : world(parent).clone().multiply(localMatrix(json.nodes[index]));
        cache.set(index, matrix); return matrix;
    };
    return world;
}

const joinPath = (base, path) => [base, path].filter(Boolean).join("/");

function remapPaths(value, remap) {
    if (!value || typeof value !== "object") return;
    for (const [key, item] of Object.entries(value)) {
        // Input bindings use device paths; legacy curves are relative to their
        // animation root and are handled separately below.
        if (["TriggerAction", "HoldAction", "LegacyClips"].includes(key)) continue;
        if (["Path", "ContainerPath", "RendererPath", "TargetTransformPath", "ReferenceTransformPath", "TargetCameraPath", "TargetAnimationPath"].includes(key)
            && typeof item === "string") value[key] = remap(item);
        else if (["TargetPaths", "TargetLightPaths", "MaterialDummyPaths"].includes(key) && Array.isArray(item)) value[key] = item.map(remap);
        else remapPaths(item, remap);
    }
}

function transformOptions(doc, next, index, conversion, newScale, root) {
    const base = decompose(localMatrix(doc.json.nodes[index]));
    for (const set of next.sets) {
        if (set.kind !== "Transform" || resolveModelVistaNodePath(doc.json, root, dataOf(set).TargetTransformPath ?? "") !== index) continue;
        for (const option of set.options) {
            const raw = option.raw;
            const p = raw.ApplyPosition && raw.LocalPosition ? new Vector3(raw.LocalPosition.X, raw.LocalPosition.Y, raw.LocalPosition.Z) : base.position;
            const q = raw.ApplyRotation && raw.LocalRotation ? new Quaternion(raw.LocalRotation.X, raw.LocalRotation.Y, raw.LocalRotation.Z, raw.LocalRotation.W).normalize() : base.rotation;
            const pose = decompose(conversion.clone().multiply(new Matrix4().compose(p, q, base.scale)));
            if (!pose.scale.toArray().every((value, i) => close(value, newScale.getComponent(i)))) {
                throw new Error("This move would distort a saved Transform option. Choose a parent with compatible scale.");
            }
            if (raw.ApplyPosition) raw.LocalPosition = vectorData(pose.position);
            if (raw.ApplyRotation) raw.LocalRotation = quaternionData(pose.rotation);
        }
    }
}

export function moveHierarchy(doc, indices, targetIndex, position, liveWorldMatrices = null) {
    const plan = hierarchyDropPlan(doc.json, indices, targetIndex, position);
    if (!plan) return false;
    if (!plan.reparented.length) { doc.json.nodes[plan.parentIndex].children = plan.children; return true; }
    const before = nodeTree(doc.json), root = before[0].index, reparented = new Set(plan.reparented);
    const animated = new Set((doc.json.animations ?? []).flatMap(clip => (clip.channels ?? [])
        .filter(channel => ["translation", "rotation", "scale"].includes(channel.target?.path)).map(channel => channel.target.node)));
    for (const set of doc.sets.filter(set => set.kind === "Animation")) {
        const data = dataOf(set), base = data.TargetAnimationPath || set.raw.Path || "";
        for (const clip of data.LegacyClips ?? []) for (const curve of clip?.Curves ?? []) {
            if (/^(m_)?local(position|rotation|eulerangles|scale)/i.test(curve.PropertyName ?? "")) animated.add(resolveModelVistaNodePath(doc.json, root, joinPath(base, curve.Path)));
        }
    }
    // Stage the entire edit so unsupported transforms never leave a partial move.
    const next = structuredClone(doc), world = worldMatrices(doc.json, plan.parents, liveWorldMatrices);
    for (const index of plan.reparented) {
        const destination = world(plan.parentIndex);
        if (!Number.isFinite(destination.determinant()) || destination.determinant() === 0) throw new Error("A parent with zero scale cannot receive parts.");
        const conversion = destination.clone().invert().multiply(world(plan.parents.get(index)));
        if (animated.has(index) && !conversion.elements.every((value, i) => close(value, i % 5 === 0 ? 1 : 0))) {
            throw new Error("This part has embedded transform animation. Move a parent group instead.");
        }
        const pose = decompose(conversion.clone().multiply(localMatrix(doc.json.nodes[index])));
        const node = next.json.nodes[index];
        delete node.matrix;
        node.translation = pose.position.toArray(); node.rotation = pose.rotation.toArray(); node.scale = pose.scale.toArray();
        transformOptions(doc, next, index, conversion, pose.scale, root);
    }
    for (const index of plan.moving) {
        const parent = next.json.nodes[plan.parents.get(index)];
        parent.children = parent.children.filter(child => child !== index);
        if (!parent.children.length) delete parent.children;
    }
    next.json.nodes[plan.parentIndex].children = plan.children;
    // Unique sibling names keep path-based State references unambiguous.
    const names = new Set(plan.children.filter(index => !reparented.has(index)).map(index => next.json.nodes[index].name));
    for (const index of plan.reparented) {
        const node = next.json.nodes[index], base = String(node.name || `Part ${index + 1}`).replaceAll("/", "_");
        let name = base, suffix = 2;
        while (names.has(name)) name = `${base} (${suffix++})`;
        node.name = name; names.add(name);
    }
    const after = new Map(nodeTree(next.json).map(node => [node.index, node.path])), pathCounts = new Map();
    for (const node of before) pathCounts.set(node.path, (pathCounts.get(node.path) ?? 0) + 1);
    for (const node of before) if (node.path !== after.get(node.index)) {
        if (pathCounts.get(node.path) !== 1) throw new Error("Parts with duplicate hierarchy paths cannot be moved between parents.");
    }
    const remap = path => {
        if (typeof path !== "string") return path;
        const index = resolveModelVistaNodePath(doc.json, root, path);
        // Resolve legacy name aliases too (e.g. Body resolves Body.001), and
        // canonicalize them before a new sibling could shadow that alias.
        return index !== null && after.get(index) !== "" ? after.get(index) : path;
    };
    for (const set of next.sets.filter(set => set.kind === "Animation")) {
        const data = dataOf(set), oldBase = data.TargetAnimationPath || set.raw.Path || "", newBase = remap(oldBase);
        for (const clip of data.LegacyClips ?? []) for (const curve of clip?.Curves ?? []) {
            const path = remap(joinPath(oldBase, curve.Path));
            if (newBase && path !== newBase && !path.startsWith(newBase + "/")) throw new Error("This move would detach a legacy animation from its target. Move its animation root as well.");
            curve.Path = newBase ? path.slice(newBase.length).replace(/^\//, "") : path;
        }
    }
    remapPaths(next.sets, remap); remapPaths(next.json.extensions?.[EXTENSION], remap);
    Object.assign(doc, next);
    return true;
}

export function hierarchySelection(json, indices) {
    if (!indices.length) return null;
    const tree = nodeTree(json), parents = new Map(), byIndex = new Map(tree.map(node => [node.index, node]));
    for (const node of tree) for (const child of json.nodes[node.index].children ?? []) parents.set(child, node.index);
    const selected = new Set(indices);
    if ([...selected].some(index => !parents.has(index) || byIndex.get(index).path.split("/").some(name => name.startsWith("__INNOWORKS_MATERIAL")))) return null;
    const hasAncestor = (index, ancestors) => {
        for (let parent = parents.get(index); parent !== undefined; parent = parents.get(parent)) if (ancestors.has(parent)) return true;
        return false;
    };
    const roots = tree.filter(node => selected.has(node.index) && !hasAncestor(node.index, selected)).map(node => node.index);
    const rootSet = new Set(roots), nodes = tree.filter(node => rootSet.has(node.index) || hasAncestor(node.index, rootSet));
    return { roots, nodes, parents, root: tree[0].index };
}

// Clone node definitions and reuse binary geometry, textures and animation
// samplers. Appending nodes keeps every existing glTF index stable.
export function duplicateHierarchy(doc, indices, liveParts = null) {
    const selection = hierarchySelection(doc.json, indices);
    if (!selection) throw new Error("Select parts below the model root first.");
    const next = structuredClone(doc), copies = new Map(), roots = new Set(selection.roots);
    const resolve = path => resolveModelVistaNodePath(doc.json, selection.root, path);
    const skinCopies = new Map();
    for (const { index } of selection.nodes) {
        copies.set(index, next.json.nodes.length);
        const node = structuredClone(doc.json.nodes[index]), live = liveParts?.get(index);
        if (live) { delete node.matrix; node.translation = [...live.translation]; node.rotation = [...live.rotation]; node.scale = [...live.scale]; }
        next.json.nodes.push(node);
    }
    for (const { index } of selection.nodes) {
        const node = next.json.nodes[copies.get(index)];
        if (node.children) node.children = node.children.map(child => copies.get(child));
        if (node.skin !== undefined) {
            if (!skinCopies.has(node.skin)) {
                const skin = structuredClone(doc.json.skins[node.skin]);
                skin.joints = skin.joints.map(joint => copies.get(joint) ?? joint);
                if (skin.skeleton !== undefined) skin.skeleton = copies.get(skin.skeleton) ?? skin.skeleton;
                next.json.skins.push(skin); skinCopies.set(node.skin, next.json.skins.length - 1);
            }
            node.skin = skinCopies.get(node.skin);
        }
        if (roots.has(index)) {
            const parent = next.json.nodes[selection.parents.get(index)];
            const names = new Set(parent.children.flatMap(child => { const name = next.json.nodes[child].name || ""; return [name, name.replace(/\.\d{3}$/, "")]; }));
            const base = `${node.name || "Part"} copy`; let name = base, suffix = 2;
            while (names.has(name)) name = `${base} (${suffix++})`;
            node.name = name;
            parent.children.splice(parent.children.indexOf(index) + 1, 0, copies.get(index));
        }
    }
    const paths = new Map(nodeTree(next.json).map(node => [node.index, node.path]));
    const remap = path => paths.get(copies.get(resolve(path))) ?? path;
    for (const clip of next.json.animations ?? []) {
        const channels = clip.channels.filter(channel => copies.has(channel.target?.node)).map(channel => {
            const result = structuredClone(channel); result.target.node = copies.get(channel.target.node); return result;
        });
        clip.channels.push(...channels);
    }
    const copiedSets = new Map(), copiedOptions = new Map();
    for (const set of doc.sets.filter(set => copies.has(resolve(set.raw.Path ?? "")))) {
        const copy = structuredClone(set), originalDefault = copy.defaultId;
        copy.id = crypto.randomUUID(); copiedSets.set(set.id, copy.id);
        for (const option of copy.options) {
            const original = option.id; option.id = crypto.randomUUID(); copiedOptions.set(original, option.id);
            if (original === originalDefault) copy.defaultId = option.id;
        }
        remapPaths(copy, remap);
        next.sets.push(copy);
    }
    for (const set of next.sets.filter(set => [...copiedSets.values()].includes(set.id))) for (const option of set.options) for (const link of option.links ?? []) {
        link.setId = copiedSets.get(link.setId) ?? link.setId;
        link.optionId = copiedOptions.get(link.optionId) ?? link.optionId;
    }
    next.deferredStateIds = [...(next.deferredStateIds ?? []), ...copiedSets.values()];
    const extension = next.json.extensions[EXTENSION], originalExtension = doc.json.extensions[EXTENSION];
    for (const key of ["MaterialBindings", "RendererSettings", "GameObjectVisibilities", "Metadata"]) {
        const records = (originalExtension[key] ?? []).filter(record => copies.has(record.NodeIndex ?? resolve(record.RendererPath ?? record.Path ?? ""))).map(record => {
            const copy = structuredClone(record); remapPaths(copy, remap);
            if (copy.NodeIndex !== undefined) copy.NodeIndex = copies.get(copy.NodeIndex);
            return copy;
        });
        if (records.length) extension[key] = [...(extension[key] ?? []), ...records];
    }
    if (originalExtension.MaterialDummyPaths) extension.MaterialDummyPaths.push(...originalExtension.MaterialDummyPaths.filter(path => copies.has(resolve(path))).map(remap));
    for (const [index, live] of liveParts ?? []) if (copies.has(index)) {
        if (!extension.GameObjectVisibilities) extension.GameObjectVisibilities = [];
        const copyIndex = copies.get(index), path = paths.get(copyIndex);
        const record = extension.GameObjectVisibilities.find(record => record.NodeIndex === copyIndex || record.Path === path);
        if (record) record.ActiveSelf = live.visible;
        else extension.GameObjectVisibilities.push({ NodeIndex: copyIndex, Path: path, ActiveSelf: live.visible });
    }
    Object.assign(doc, next);
    return { selectedNodes: selection.roots.map(index => copies.get(index)) };
}

export function deleteHierarchy(doc, indices) {
    const selection = hierarchySelection(doc.json, indices);
    if (!selection) throw new Error("Select parts below the model root first.");
    const removed = new Set(selection.nodes.map(node => node.index)), next = structuredClone(doc);
    const resolve = path => resolveModelVistaNodePath(doc.json, selection.root, path);
    const deletedPath = path => removed.has(resolve(path));
    const survivors = nodeTree(doc.json).filter(node => !removed.has(node.index));
    for (const { index } of survivors) {
        const skin = doc.json.skins?.[doc.json.nodes[index].skin];
        if (skin?.joints.some(joint => removed.has(joint)) || removed.has(skin?.skeleton)) throw new Error("These bones are used by a mesh outside the selection. Select that mesh as well before deleting.");
    }
    for (const index of selection.roots) {
        const parent = next.json.nodes[selection.parents.get(index)];
        parent.children = parent.children.filter(child => child !== index);
        if (!parent.children.length) delete parent.children;
    }
    const clips = doc.json.animations ?? [], clipIndices = new Map();
    const animations = clips.flatMap((clip, index) => {
        const copy = structuredClone(clip); copy.channels = copy.channels.filter(channel => !removed.has(channel.target?.node));
        if (!copy.channels.length) return [];
        clipIndices.set(index, clipIndices.size); return [copy];
    });
    const removedSets = new Set();
    for (const set of next.sets) {
        const data = dataOf(set);
        let discard = deletedPath(set.raw.Path ?? "");
        if (set.kind === "Transform") discard = discard || deletedPath(data.TargetTransformPath ?? "");
        if (set.kind === "Camera") discard = discard || deletedPath(data.ReferenceTransformPath ?? set.raw.Path ?? "") || deletedPath(data.TargetCameraPath ?? "");
        if (set.kind === "Material" && data.TargetRenderers?.length) {
            data.TargetRenderers = data.TargetRenderers.filter(ref => !deletedPath(ref.Path)); discard = discard || !data.TargetRenderers.length;
        }
        if (set.kind === "Activation") {
            for (const option of set.options) option.raw.TargetPaths = (option.raw.TargetPaths ?? []).filter(path => !deletedPath(path));
            discard = discard || !set.options.some(option => option.raw.TargetPaths.length);
        }
        if (set.kind === "Light" && data.TargetLightPaths) {
            data.TargetLightPaths = data.TargetLightPaths.filter(path => !deletedPath(path)); discard = discard || !data.TargetLightPaths.length;
        }
        if (set.kind === "Animation") {
            const base = data.TargetAnimationPath || set.raw.Path || ""; discard = discard || deletedPath(base);
            const legacy = data.LegacyClips ?? [], legacyIndices = new Map();
            const legacyClips = legacy.flatMap((clip, index) => {
                if (!clip) return [];
                const copy = structuredClone(clip); copy.Curves = (copy.Curves ?? []).filter(curve => !deletedPath(joinPath(base, curve.Path)));
                if (!copy.Curves.length) return [];
                legacyIndices.set(index, legacyIndices.size); return [copy];
            });
            if (!discard && legacy.length && clips.length && (animations.length !== clips.length || legacyClips.length !== legacy.filter(Boolean).length)) {
                throw new Error("Delete this part's Animation State first before changing its mixed native and legacy animation bindings.");
            }
            // Native and legacy clips share an index namespace after Insert.
            // Keep their original slots when native animations are present.
            if (data.LegacyClips) data.LegacyClips = clips.length
                ? legacy.map((clip, index) => clip ? legacyClips[legacyIndices.get(index)] ?? null : null) : legacyClips;
            discard = discard || !(animations.length || legacyClips.length);
            for (const option of set.options) {
                const index = option.raw.AnimationIndex ?? -1;
                option.raw.AnimationIndex = index < 0 ? -1 : index < clips.length ? clipIndices.get(index) ?? -1
                    : clips.length ? (legacyIndices.has(index) ? index : -1) : legacyIndices.get(index) ?? -1;
            }
        }
        if (discard) removedSets.add(set.id);
    }
    if (clips.length) { if (animations.length) next.json.animations = animations; else delete next.json.animations; }
    next.sets = next.sets.filter(set => !removedSets.has(set.id));
    for (const set of next.sets.filter(set => set.kind === "Group")) for (const option of set.options) option.links = (option.links ?? []).filter(link => !removedSets.has(link.setId));
    next.deferredStateIds = (next.deferredStateIds ?? []).filter(id => !removedSets.has(id));
    const extension = next.json.extensions[EXTENSION];
    for (const key of ["MaterialBindings", "RendererSettings", "GameObjectVisibilities", "Metadata"]) if (extension[key]) {
        extension[key] = extension[key].filter(record => !removed.has(record.NodeIndex ?? resolve(record.RendererPath ?? record.Path ?? "")));
    }
    if (extension.MaterialDummyPaths) extension.MaterialDummyPaths = extension.MaterialDummyPaths.filter(path => !deletedPath(path));
    Object.assign(doc, next);
    return { selectedNodes: [] };
}
